// Sugalarm — web-dashboard "Alarm activity" panel (cycle 179).
//
// WEB FRAME ONLY (mounted only by the web-gated DashboardScreen). A recent
// history of alarm EPISODES for the viewed member + selected window: what
// fired (severity + value), when, and how it resolved (recovered with
// duration / acknowledged / snoozed / silenced / unresolved). One row =
// one episode — the alerts engine is edge-triggered, so a sustained
// out-of-range stretch is a single row, not one per reading.
//
// READ-ONLY. Sourced entirely from the `/api/alerts?hours=N` rows the
// dashboard already loads — no new endpoint, no alarm-lifecycle change.
//
// CHANNELS (push / SMS / voice): deliberately OMITTED. Per-rail delivery
// rows DO exist server-side (`notification_delivery`, cycle 96) but are
// surfaced only through the admin-gated `/api/admin/notifications/*`
// routes — no member-accessible endpoint exposes per-alert channels, and
// this cycle reads existing member-facing data only (no new backend to
// synthesize the shape). So the panel shows severity/value/time/resolution
// and stops there. See the cycle-179 CHANGELOG entry for the decision.

import { useMemo } from "react";

import { Pill, type PillTone } from "../../components/ui/Pill";
import { displayValue, fmtAge, fmtEntryTime } from "../../lib/glucose";
import type { Alert } from "../../lib/types";
import styles from "./AlarmActivityPanel.module.css";

// Cap the rendered rows so a busy multi-day window can't build a giant
// DOM; the panel notes how many are hidden. Episodes are sparse, so this
// rarely bites.
const MAX_ROWS = 50;

function toneForLevel(level: string): PillTone {
  if (level === "urgent_low" || level === "urgent_high") return "urgent";
  if (level === "low" || level === "high") return "warn";
  return "neutral";
}

function severityLabel(level: string): string {
  switch (level) {
    case "urgent_low":
      return "Urgent low";
    case "low":
      return "Low";
    case "high":
      return "High";
    case "urgent_high":
      return "Urgent high";
    default:
      return level.replace(/_/g, " ");
  }
}

function fmtDuration(min: number): string {
  if (min < 1) return "<1 min";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

interface Resolution {
  label: string;
  tone: PillTone;
  /** Optional secondary note, e.g. "· acknowledged" alongside a recovery. */
  note?: string;
}

function resolutionOf(a: Alert): Resolution {
  if (a.recovered_at) {
    const t = new Date(a.triggered_at).getTime();
    const r = new Date(a.recovered_at).getTime();
    const dur =
      !Number.isNaN(t) && !Number.isNaN(r) && r > t
        ? Math.round((r - t) / 60000)
        : null;
    return {
      label: dur != null ? `Recovered in ${fmtDuration(dur)}` : "Recovered",
      tone: "ok",
      note: a.acknowledged_at ? "acknowledged" : undefined,
    };
  }
  if (a.acknowledged_at) return { label: "Acknowledged", tone: "ok" };
  if (a.silenced_at) return { label: "Silenced", tone: "neutral" };
  if (a.snoozed_until) return { label: "Snoozed", tone: "neutral" };
  return { label: "Unresolved", tone: "warn" };
}

interface Props {
  alerts: Alert[];
  units: "mg/dL" | "mmol/L";
  h24: boolean;
  now: number;
  windowLabel: string;
}

export function AlarmActivityPanel({ alerts, units, h24, now, windowLabel }: Props) {
  // Most-recent first. Copy before sorting — never mutate the prop.
  const sorted = useMemo(
    () =>
      [...alerts].sort(
        (a, b) =>
          new Date(b.triggered_at).getTime() - new Date(a.triggered_at).getTime(),
      ),
    [alerts],
  );

  const shown = sorted.slice(0, MAX_ROWS);
  const hidden = sorted.length - shown.length;

  return (
    <section className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.cardLabel}>Alarm activity</span>
        <span className={styles.hint}>
          {sorted.length === 0
            ? `last ${windowLabel}`
            : `${sorted.length} ${sorted.length === 1 ? "episode" : "episodes"} · last ${windowLabel}`}
        </span>
      </div>

      {sorted.length === 0 ? (
        <div className={styles.empty}>
          No alarms in the last {windowLabel}. Sugar Alarm fires on level
          transitions only — one row per episode, not one per reading.
        </div>
      ) : (
        <>
          <ul className={styles.list}>
            {shown.map((a) => {
              const res = resolutionOf(a);
              const ts = new Date(a.triggered_at).getTime();
              const ageMin = Number.isNaN(ts)
                ? null
                : Math.max(0, Math.floor((now - ts) / 60000));
              return (
                <li key={a.id} className={styles.row}>
                  <Pill tone={toneForLevel(a.level)}>{severityLabel(a.level)}</Pill>
                  <span className={`${styles.value} tnum`}>
                    {displayValue(a.sgv, units)}
                    <span className={styles.unit}> {units}</span>
                  </span>
                  <span className={styles.time}>
                    {fmtEntryTime(a.triggered_at, now, h24)}
                    {ageMin != null ? (
                      <span className={styles.age}> · {fmtAge(ageMin)}</span>
                    ) : null}
                  </span>
                  <span className={styles.resolution}>
                    <Pill tone={res.tone}>{res.label}</Pill>
                    {res.note ? <span className={styles.resNote}>· {res.note}</span> : null}
                  </span>
                </li>
              );
            })}
          </ul>
          {hidden > 0 ? (
            <div className={styles.more}>
              + {hidden} more {hidden === 1 ? "episode" : "episodes"} in this window
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
